'use client';
import React, { useEffect, useRef, useState } from 'react';

import GlobeIcon from '@/assets/icons/globe.svg';
import SearchIcon from '@/assets/icons/search.svg';
import ProfileIcon from '@/assets/icons/profile.svg';
import CloseIcon from '@/assets/icons/close.svg';

import { IMeetingPlacePageSearchResults } from '@/lib/fragments/meetingPlacePageSearchResults';
import { ILibrarySocialMediaLinks } from '@/lib/fragments/librarySocialMediaLinks';
import { LanguageSelectorWidget } from '../language-selector/language-selector-widget';
import { isBrowser } from '@/helpers/is-browser';
import { FoldOutMenu } from '../fold-out-menu';

import { extractQueryParams } from '@/helpers/floor-plan/floor-plan-helpers';
import { useOnClickOutside } from '@/hooks';
import { ILibraryMeetingPlace } from '@/lib/fragments/libraryMeetingPlace';
import { ILibraryMenuGroup } from '@/lib/fragments/libraryMenuGroup';

// import { PageContext } from '@/templates/page-context.interface';
import { TextAndImageCardBlockProps } from '../blocks/text-and-image-card-block/text-and-image-card-block';
import { usePathname, useSearchParams } from 'next/navigation';
import { SearchWidget } from '../search/search-widget';
import { useUser } from '@/providers/UserProvider';
import { checkIfValidUser } from '@/helpers/utils';
import { useIsMobile } from '@/hooks/use-is-mobile';
import UserProfile from '../user-profile/user-profile';
import LoginRegister from '../user-profile/login-register';
import { SideModal } from '../ui/side-modal/side-modal';
import { SideModalBody } from '../ui/side-modal/side-modal-body';
import Link from 'next/link';

interface TabBarProps {
  menuGroups: ILibraryMenuGroup[];
  menuCards: TextAndImageCardBlockProps[];
  logo: any;
  socialMediaLinks: ILibrarySocialMediaLinks;
  meetingPlace: ILibraryMeetingPlace;
  node_locale: string;
  searchResults?: IMeetingPlacePageSearchResults;
  hrefLangTags: any;
  config: any;
}

export const TabBar = ({
  menuGroups,
  menuCards,
  logo,
  socialMediaLinks,
  meetingPlace,
  node_locale,
  searchResults,
  hrefLangTags,
  config,
}: TabBarProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const ref = useRef<HTMLDivElement>(null);
  const refSearch = useRef<HTMLDivElement>(null);
  const refLanguageSelector = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [showMenu, setShowMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [routeExists, setRouteExists] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [sliderOpen, setSliderOpen] = useState(false);
  const { user, isLoggedIn, cachedInitials } = useUser();

  const loginEnabled = config?.NEXT_PUBLIC_ICIM_LOGIN_ENABLED === 'true';
  const tempLoginEnabled = config?.NEXT_PUBLIC_TEMP_LOGIN_ENABLED === 'true';
  const isLivliFamily = config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true';
  const prefix = config?.GATSBY_SITE_PATH_PREFIX;

  useOnClickOutside(ref, () => setShowMenu(false));
  useOnClickOutside(refSearch, () => setShowSearch(false));
  useOnClickOutside(
    refLanguageSelector,
    () => setShowLanguageSelector(false),
    false,
    ['language-selector-trigger-button'],
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    showMenu
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [showMenu]);

  useEffect(() => {
    showSearch
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [showSearch]);

  useEffect(() => {
    sliderOpen
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [sliderOpen]);

  const [scrollDown, setScrollDown] = useState(false);

  // Hides mobile menu on scroll down. Shows on scroll up.
  // https://stackoverflow.com/a/64371350/790522

  let lastScrollPos: number = (isBrowser && window.scrollY) || 0;
  const setScroll = () => {
    if ((isBrowser && window.scrollY < 0) || (isBrowser && lastScrollPos < 0))
      return; // This if statement fixes a bug on iOS Safari. Without it the hamburger menu will be hidden when user scrolls up so fast that the page "bounces".
    setScrollDown(isBrowser && window.scrollY > lastScrollPos);
    lastScrollPos = (isBrowser && window.scrollY) || 0;
  };

  useEffect(() => {
    document.addEventListener('scroll', setScroll);
    return () => {
      document.removeEventListener('scroll', setScroll);
    };
  });

  useEffect(() => {
    const params = extractQueryParams(searchParams);
    if (params.kioskId != '') {
      setRouteExists(true);
    } else {
      setRouteExists(false);
    }
  }, [searchParams]);

  const toggleMenu = () => setShowMenu(!showMenu);
  const toggleSearch = () => setShowSearch(!showSearch);
  const toggleLanguageSelector = () =>
    setShowLanguageSelector(!showLanguageSelector);

  // Following effect hides the menu when user clicks something in it. Before the menu wasnt closed if you clicked a link/card pointing to the same page you are on.

  useEffect(() => {
    if (showMenu === false) return;

    // Close menu when pathname changes
    setShowMenu(false);
  }, [pathname]);

  // cached initials available instantly from localStorage — no flicker
  const initials = isLoggedIn
    ? (user?.firstName?.[0] ?? '')?.toUpperCase() +
    (user?.lastName?.[0] ?? '')?.toUpperCase()
    : cachedInitials;

  const renderIconContent = () => {
    if (initials) {
      // Show initials immediately — even before full user loads
      return (
        <div className="bg-brand-yellow-1 w-[50%] h-[50%] text-center lg:w-full lg:h-full rounded-full content-center lg:hover:bg-brand-yellow-2 text-buttonlabelxs font-sans text-[black]">
          {initials}
        </div>
      );
    }
    // Confirmed not logged in
    return <ProfileIcon />;
  };

  const profileUrl = (() => {
    // const origin =
    //   process.env.NEXT_PUBLIC_ENV == 'development'
    //     ? isBrowser && window.location.origin
    //     : isBrowser
    //       ? window.location.origin
    //       : '';
    const origin = isBrowser ? window.location.origin : '';
    const profilePrefixes = ['matosinhos', 'algarve'];

    const prefix =
      process.env.NEXT_PUBLIC_ENV == 'development' && !profilePrefixes.includes(config?.GATSBY_SITE_PATH_PREFIX)
        ? ''
        : config?.GATSBY_SITE_PATH_PREFIX;
    const mpid = meetingPlace?.businessUnitCode;
    const path = [prefix, node_locale, 'profile'].filter(Boolean).join('/');
    return `${origin}/${path}?mpid=${mpid}`;
  })();

  return (
    <nav
      aria-label="Main menu"
      className={`fixed bottom-5 left-1/2 z-50 flex min-w-max -translate-x-1/2 transform items-center justify-around rounded-xl bg-brand-white shadow-default transition-all max-lg:flex-row-reverse lg:absolute lg:bottom-auto lg:left-auto lg:right-0 lg:top-0 lg:w-27 lg:min-w-0 lg:transform-none lg:rounded-none lg:bg-transparent lg:shadow-transparent ${scrollDown ? 'translate-y-24 lg:translate-y-0' : ''
        }`}
      data-elastic-exclude
    >
      {!routeExists && (
        <>
          <button
            onClick={toggleMenu}
            type="button"
            className="box-content order-1 grid h-15 w-15 place-items-center content-center focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 lg:fixed lg:top-6 lg:rounded-full lg:bg-brand-yellow-1 lg:transition-all lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark"
            aria-expanded={showMenu}
            aria-controls="main-menu"
            aria-label="Open main menu"
          >
            <span className="m-px h-1 w-5 rounded-sm bg-brand-grey-3 lg:bg-brand-grey-4"></span>
            <span className="m-px h-1 w-5 rounded-sm bg-brand-grey-3 lg:bg-brand-grey-4"></span>
            <span className="m-px h-1 w-5 rounded-sm bg-brand-grey-3 lg:bg-brand-grey-4"></span>
          </button>
          <FoldOutMenu
            el={ref}
            menuClass={
              showMenu
                ? ''
                : 'translate-y-[calc(100%+10rem)] lg:translate-y-0 lg:hidden'
            }
            toggleMenu={toggleMenu}
            logo={logo}
            menuGroups={menuGroups}
            menuCards={menuCards}
            socialMediaLinks={socialMediaLinks}
            meetingPlace={meetingPlace}
            hrefLangTags={hrefLangTags}
            onLinkClick={() => setShowMenu(false)}
          />
        </>
      )}

      {searchResults && !routeExists && (
        <button
          type="button"
          onClick={toggleSearch}
          className={`grid h-15 w-15 order-3 place-items-center text-icon-dark outline-0 transition-all lg:fixed lg:top-28 lg:h-10 lg:w-10 lg:rounded-full lg:bg-brand-white lg:hover:bg-neutral-grey-100 lg:focus-visible:bg-brand-white lg:focus-visible:outline lg:focus-visible:outline-3 lg:focus-visible:outline-offset-3 lg:focus-visible:outline-icon-dark lg:active:bg-neutral-grey-200
        ${showSearch ? 'hidden' : ''}`}
          aria-expanded={showSearch}
          aria-controls="search-widget"
          aria-label="Open search"
        >
          <SearchIcon />
        </button>
      )}
      {showSearch && searchResults && (
        <SearchWidget
          el={refSearch}
          logo={logo}
          toggleSearch={toggleSearch}
          searchClass={
            showSearch ? '' : 'translate-y-full lg:-translate-y-[7rem]'
          }
          node_locale={node_locale}
          meetingPlace={meetingPlace}
          searchResults={searchResults}
        />
      )}

      {tempLoginEnabled && initials && (
        <Link
          id="temp-profile-selector-trigger-button"
          type="button"
          href={
            prefix
              ? `/${prefix}/${node_locale}/livli-family-success`
              : `/${node_locale}/livli-family-success`
          }
          className="grid h-15 w-15 order-2 place-items-center text- text-icon-dark outline-0 transition-all lg:fixed lg:top-60 lg:h-10 lg:w-10 lg:rounded-full lg:hover:bg-neutral-grey-100 lg:focus-visible:bg-brand-white lg:focus-visible:outline lg:focus-visible:outline-3 lg:focus-visible:outline-offset-3 lg:focus-visible:outline-icon-dark lg:active:bg-neutral-grey-200"
          aria-controls="profile-selector"
          aria-label="User Profile"
        >
          <div className="bg-brand-yellow-1 w-[50%] h-[50%] text-center lg:w-full lg:h-full rounded-full content-center lg:hover:bg-brand-yellow-2 text-buttonlabelxs font-sans text-[black]">
            {initials}
          </div>
        </Link>
      )}

      {loginEnabled && (
        <>
          {isLivliFamily ? (
            <a
              id="profile-selector-trigger-button"
              type="button"
              href={profileUrl}
              // onClick={() => setSliderOpen(true)}
              className="grid h-15 w-15 order-2 place-items-center text- text-icon-dark outline-0 transition-all lg:fixed lg:top-60 lg:h-10 lg:w-10 lg:rounded-full lg:hover:bg-neutral-grey-100 lg:focus-visible:bg-brand-white lg:focus-visible:outline lg:focus-visible:outline-3 lg:focus-visible:outline-offset-3 lg:focus-visible:outline-icon-dark lg:active:bg-neutral-grey-200"
              aria-expanded={sliderOpen}
              aria-controls="profile-selector"
              aria-label="User Profile"
            >
              {renderIconContent()}
            </a>
          ) : (
            <button
              id="profile-selector-trigger-button"
              type="button"
              onClick={() => setSliderOpen(true)}
              className="grid h-15 w-15 order-2 place-items-center text- text-icon-dark outline-0 transition-all lg:fixed lg:top-60 lg:h-10 lg:w-10 lg:rounded-full lg:hover:bg-neutral-grey-100 lg:focus-visible:bg-brand-white lg:focus-visible:outline lg:focus-visible:outline-3 lg:focus-visible:outline-offset-3 lg:focus-visible:outline-icon-dark lg:active:bg-neutral-grey-200"
              aria-expanded={sliderOpen}
              aria-controls="profile-selector"
              aria-label="User Profile"
            >
              {renderIconContent()}
            </button>
          )}
          {isMobile && sliderOpen ? (
            <>
              <div className="fixed bg-none !h-auto bottom-0 left-0 z-50 -mb-5 ml-[50%] w-screen -translate-x-1/2 rounded-t-xxl bg-brand-white shadow-default transition-all duration-300 ease-out lg:bottom-auto lg:top-0 lg:mb-auto lg:ml-0 lg:max-h-full lg:w-full lg:translate-x-0 lg:animate-fadeIn lg:overflow-y-auto lg:rounded-b-xxl lg:rounded-t-none">
                <button
                  className={
                    'fixed -top-20 right-4 z-40 grid h-12 w-12 place-items-center rounded-full bg-brand-grey-1 outline-0 transition-all focus:bg-brand-grey-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-brand-grey-2 lg:right-6 lg:top-6 lg:h-15 lg:w-15 lg:bg-brand-yellow-1 lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark'
                  }
                  onClick={() => setSliderOpen(false)}
                >
                  <CloseIcon />
                  <span className="sr-only">Close menu</span>
                </button>
                <div className="mx-8">
                  {user && checkIfValidUser(user) ? (
                    <UserProfile
                      user={user}
                      node_locale={node_locale}
                      brandId={meetingPlace?.businessUnitCode}
                      config={config}
                    />
                  ) : (
                    <LoginRegister
                      node_locale={node_locale}
                      brandId={meetingPlace?.businessUnitCode}
                      config={config}
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <SideModal
                isOpen={sliderOpen}
                handleClose={() => setSliderOpen(false)}
              >
                <SideModalBody bodyClass="h-full !my-0 !py-16">
                  {user && checkIfValidUser(user) ? (
                    <UserProfile
                      user={user}
                      node_locale={node_locale}
                      brandId={meetingPlace?.businessUnitCode}
                      config={config}
                    />
                  ) : (
                    <LoginRegister
                      node_locale={node_locale}
                      brandId={meetingPlace?.businessUnitCode}
                      config={config}
                    />
                  )}
                </SideModalBody>
              </SideModal>
            </>
          )}
        </>
      )}

      {meetingPlace?.showLanguageSelector && (
        <>
          <button
            id="language-selector-trigger-button"
            type="button"
            onClick={toggleLanguageSelector}
            className="grid h-15 w-15 place-items-center text-icon-dark outline-0 transition-all max-lg:hidden lg:fixed lg:top-44 lg:h-10 lg:w-10 lg:rounded-full lg:hover:bg-neutral-grey-100 lg:focus-visible:bg-brand-white lg:focus-visible:outline lg:focus-visible:outline-3 lg:focus-visible:outline-offset-3 lg:focus-visible:outline-icon-dark lg:active:bg-neutral-grey-200"
            aria-expanded={showLanguageSelector}
            aria-controls="language-selector"
            aria-label="Select language"
          >
            <GlobeIcon className="w-5 h-5" />
          </button>

          <LanguageSelectorWidget
            el={refLanguageSelector}
            hrefLangTags={hrefLangTags}
            node_locale={node_locale}
            languageSelectorClass={
              showLanguageSelector
                ? 'fixed right-7 top-56 max-lg:hidden'
                : 'hidden'
            }
            variant="dropdown"
            primaryLanguage={meetingPlace?.primaryLanguage}
            secondaryLanguages={meetingPlace?.languagesCollection?.items ?? []}
          />
        </>
      )}
    </nav>
  );
};
